import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({ 
  token, 
  currentImage = '', 
  onImageUploaded, 
  multiple = false,
  label = 'Obrázok'
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads')) return `${API_URL}${url}`;
    return url;
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Povolené formáty: JPG, PNG, GIF, WebP');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Maximálna veľkosť súboru je 10MB');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const fullUrl = getFullUrl(data.url);
        setPreview(fullUrl);
        onImageUploaded(fullUrl);
        toast.success('Obrázok nahraný!');
      } else {
        toast.error(data.detail || 'Chyba pri nahrávaní');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Chyba pri nahrávaní obrázka');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onImageUploaded(url);
  };

  const clearImage = () => {
    setPreview('');
    onImageUploaded('');
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="relative inline-block">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-40 h-40 object-cover rounded-lg border shadow-sm"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/160?text=Error';
            }}
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2 right-2 bg-green-500 text-white p-1 rounded-full">
            <CheckCircle className="h-4 w-4" />
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          dragActive 
            ? 'border-orange-500 bg-orange-50' 
            : 'border-gray-300 hover:border-orange-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="flex flex-col items-center py-4">
            <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-2" />
            <p className="text-sm text-gray-600">Nahrávam...</p>
          </div>
        ) : (
          <>
            <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-2">
              Pretiahnite obrázok sem alebo
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              id={`image-upload-${label}`}
              data-testid="image-file-input"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="select-image-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Vybrať súbor
            </Button>
          </>
        )}
      </div>

      {/* URL Input (alternative) */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">alebo URL:</span>
        <input
          type="text"
          value={preview || ''}
          onChange={handleUrlChange}
          placeholder="https://example.com/image.jpg"
          className="flex-1 px-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          data-testid="image-url-input"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
